import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Award, Calendar, ShieldCheck, Star } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth';
import {
  SPECIALTY_LABELS,
  TASK_TYPE_LABELS,
  SENIORITY_LABELS,
  type Specialty,
  type Seniority,
  type TaskType,
} from '@parecer/shared';
import { getInitials, formatBRL } from '@/lib/utils';

interface PublicCertification {
  id: string;
  title: string;
  issuer: string;
  issuedYear?: number;
  verified?: boolean;
}

interface LawyerDetail {
  userId: string;
  name: string;
  bio?: string;
  oab: string;
  oabState: string;
  linkedinUrl?: string;
  bookingUrl?: string;
  specialties: Specialty[];
  seniority?: Seniority;
  certifications?: PublicCertification[];
  averageRating?: number;
  totalReviews?: number;
  totalCompletedTasks?: number;
  pricing?: Array<{
    taskType: TaskType;
    basePrice: number;
    description?: string;
    subType?: string;
    estimatedDeliveryHours?: number;
    estimatedDurationMinutes?: number;
    accessDurationDays?: number;
    slaResponseHours?: number;
    messageLimit?: number;
    active?: boolean;
  }>;
}

export function AdvogadoPerfil() {
  const { lawyerId } = useParams();
  const role = useAuthStore((s) => s.user?.role);
  const { data, isLoading, error } = useQuery({
    queryKey: ['lawyer', lawyerId],
    queryFn: () => api.get<LawyerDetail>(`/lawyers/${lawyerId}`, { authenticated: false }),
    enabled: Boolean(lawyerId),
  });

  // Logged-out / lawyer / admin all bounce through login if they try to hire.
  // Only authenticated clientes go straight to the new-demand flow.
  function hireHref(taskType: string): string {
    const base = `/app/demanda/nova?lawyerId=${lawyerId}&taskType=${taskType}`;
    if (role === 'users') return base;
    return `/login?from=${encodeURIComponent(base)}`;
  }

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-4xl py-16">
        {isLoading && <p className="text-muted-foreground">Carregando…</p>}
        {error && <p className="text-muted-foreground">Não foi possível carregar este perfil.</p>}
        {data && (
          <>
            <div className="flex items-start gap-6">
              <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center text-2xl font-serif">
                {getInitials(data.name)}
              </div>
              <div className="flex-1">
                <h1 className="font-serif text-display-lg text-balance">{data.name}</h1>
                <p className="text-muted-foreground mt-1">
                  OAB/{data.oabState} {data.oab}
                  {data.linkedinUrl && (
                    <>
                      {' · '}
                      <a href={data.linkedinUrl} target="_blank" rel="noreferrer" className="hover:text-foreground underline">
                        LinkedIn
                      </a>
                    </>
                  )}
                </p>
                {data.averageRating !== undefined && (
                  <p className="mt-2 inline-flex items-center gap-1 text-sm">
                    <Star className="h-4 w-4 fill-accent text-accent" />
                    {data.averageRating.toFixed(1)} <span className="text-muted-foreground">({data.totalReviews ?? 0} avaliações · {data.totalCompletedTasks ?? 0} entregas)</span>
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {data.seniority && (
                    <Badge variant="accent" className="gap-1">
                      {SENIORITY_LABELS[data.seniority]}
                    </Badge>
                  )}
                  {data.specialties.map((s) => (
                    <Badge key={s} variant="muted">{SPECIALTY_LABELS[s]}</Badge>
                  ))}
                </div>
              </div>
            </div>

            {data.certifications && data.certifications.filter((c) => c.verified).length > 0 && (
              <Card className="mt-12">
                <CardContent className="pt-6">
                  <h2 className="font-serif text-xl mb-3 flex items-center gap-2">
                    <Award className="h-5 w-5 text-accent" />
                    Certificações verificadas
                  </h2>
                  <ul className="space-y-2">
                    {data.certifications
                      .filter((c) => c.verified)
                      .map((c) => (
                        <li key={c.id} className="flex items-start gap-3 text-sm">
                          <ShieldCheck className="h-4 w-4 text-success mt-0.5 flex-none" />
                          <div>
                            <p className="font-medium">{c.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {c.issuer}
                              {c.issuedYear ? ` · ${c.issuedYear}` : ''}
                            </p>
                          </div>
                        </li>
                      ))}
                  </ul>
                  <p className="text-[11px] text-muted-foreground mt-4">
                    Comprovantes validados pela equipe Parecer.ai.
                  </p>
                </CardContent>
              </Card>
            )}

            {data.bio && (
              <Card className="mt-12">
                <CardContent className="pt-6">
                  <h2 className="font-serif text-xl mb-3">Sobre</h2>
                  <p className="text-muted-foreground whitespace-pre-line text-pretty">{data.bio}</p>
                </CardContent>
              </Card>
            )}

            {(() => {
              const activePricing = (data.pricing ?? []).filter(
                (p) => (p as { active?: boolean }).active !== false && p.basePrice > 0,
              );
              if (activePricing.length === 0) return null;
              return (
                <Card className="mt-6">
                  <CardContent className="pt-6">
                    <h2 className="font-serif text-xl mb-3">Tabela de preços</h2>
                    <ul className="divide-y divide-border/40">
                      {activePricing.map((p, i) => {
                        const isChat = p.taskType === 'chat_consultation';
                        const isCallish = p.estimatedDurationMinutes !== undefined;
                        return (
                          <li
                            key={`${p.taskType}-${p.subType ?? ''}-${i}`}
                            className="py-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">
                                {TASK_TYPE_LABELS[p.taskType]}
                                {p.subType && (
                                  <span className="text-muted-foreground font-normal">
                                    {' — '}{p.subType}
                                  </span>
                                )}
                              </p>
                              {p.description && (
                                <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                              )}
                              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                {p.estimatedDeliveryHours && (
                                  <span>
                                    Entrega em até{' '}
                                    {p.estimatedDeliveryHours < 24
                                      ? `${p.estimatedDeliveryHours}h`
                                      : `${Math.ceil(p.estimatedDeliveryHours / 24)} dia(s)`}
                                  </span>
                                )}
                                {p.estimatedDurationMinutes && (
                                  <span>Sessão de {p.estimatedDurationMinutes} min</span>
                                )}
                                {p.accessDurationDays && (
                                  <span>
                                    {p.accessDurationDays} dia(s) de acesso
                                  </span>
                                )}
                                {isChat && p.slaResponseHours && (
                                  <span>Resposta em até {p.slaResponseHours}h</span>
                                )}
                                {isChat && p.messageLimit && (
                                  <span>Até {p.messageLimit} msgs</span>
                                )}
                              </div>
                              {isCallish && data.bookingUrl && (
                                <a
                                  href={data.bookingUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline"
                                >
                                  <Calendar className="h-3 w-3" /> Ver horários na agenda
                                </a>
                              )}
                            </div>
                            <div className="flex items-center gap-3 sm:flex-none">
                              <span className="font-serif text-lg">
                                {formatBRL(Math.round(p.basePrice * 1.15))}
                              </span>
                              <Button asChild variant="accent" size="sm" className="gap-1.5">
                                <Link to={hireHref(p.taskType)}>
                                  Contratar
                                  <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-[11px] text-muted-foreground mt-4">
                      Valores incluem a taxa de 15% da plataforma. Pagamento processado via Pagar.me com escrow.
                      Em caso de divergência (ex.: serviço não prestado, SLA descumprido),{' '}
                      <Link to="/termos-mediacao" className="hover:text-foreground underline">
                        a Parecer.ai medeia
                      </Link>
                      .
                      {role !== 'users' && ' Entre na sua conta para contratar.'}
                    </p>
                  </CardContent>
                </Card>
              );
            })()}

            {/* TODO(parecer-mvp): listar reviews públicas */}
          </>
        )}
      </div>
    </MarketingLayout>
  );
}
